import { type Organization } from "@entities";
import { Autocomplete, Button, Stack, TextField } from "@mui/material";
import { organizationManagerRepo, organizationRepo } from "@repositories";
import { linko } from "@shared/linko";
import { BaseRoles } from "@shared/Roles";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { remult } from "remult";
import { useUser } from "../../contexts/UserProvider";
import "./SuperAdmin.scss";

export const SuperAdmin: FC = () => {
  const { updatedCurrentUser } = useUser();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [orgInput, setOrgInput] = useState("");
  const navigate = useNavigate();

  const searchOrganizations = useCallback(async (query: string) => {
    const q = query.trim();
    const list = await organizationRepo.find({
      ...(q.length > 0 ? { where: { name: { $contains: q } } } : {}),
      orderBy: { name: "asc" },
      limit: q.length > 0 ? 30 : 200,
    });
    setOrganizations(list);
  }, []);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void searchOrganizations(orgInput);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [orgInput, searchOrganizations]);

  const autocompleteOptions = useMemo(() => {
    if (selectedOrg && !organizations.some((org) => org.id === selectedOrg.id)) {
      return [selectedOrg, ...organizations];
    }
    return organizations;
  }, [organizations, selectedOrg]);

  const organizationLogin = async () => {
    const organization = selectedOrg;
    const organizationManagers = await organizationManagerRepo.find({
      where: {
        organization: { $id: organization?.id || "111" },
      },
    });
    const manager = organizationManagers.find((manager) =>
      manager?.roles?.includes(BaseRoles.SuperAdmin),
    );
    if (!organization || !manager) return;

    remult.user = {
      id: organization.id,
      name: organization.name,
      roles: ["organization"],
      organizationId: organization.id,
      memberId: manager.id,
      tableSource: "organization-manager",
    };
    await linko.organizationManager.superDuperAdmin.superAdminLoginToOrganization(
      {
        organizationId: organization.id,
      },
    );
    await updatedCurrentUser({
      name: manager.name,
      profileImageId: manager.profileImageId,
      email: manager.email,
      phoneNumber: manager.phoneNumber,
    } as any);
    navigate("/volunteers");
  };

  return (
    <Stack className="welcome-container">
      <Outlet />
      <Stack alignItems="center" gap="2vh">
        <label>מעבר לתצוגת ארגון</label>
        <Autocomplete
          options={autocompleteOptions}
          value={selectedOrg}
          onChange={(_, org) => setSelectedOrg(org)}
          inputValue={orgInput}
          onInputChange={(_, value, reason) => {
            if (reason === "reset" && selectedOrg) {
              setOrgInput(selectedOrg.name);
              return;
            }
            setOrgInput(value);
          }}
          getOptionLabel={(org) => org.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          noOptionsText="הקלד לחיפוש ארגון"
          sx={{ width: "15rem" }}
          className="super-admin-organization-select"
          renderInput={(params) => (
            <TextField {...params} placeholder="בחירת ארגון" />
          )}
        />
        <Button
          sx={{ width: "15rem" }}
          variant="contained"
          onClick={organizationLogin}
        >
          בחירת ארגון
        </Button>
      </Stack>
    </Stack>
  );
};
